import SearchIcon from '@mui/icons-material/Search'
import { Stack } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import React, { useState } from 'react'
import { UserRpps } from '@saas/interfaces/core'
import useSearchRpps from '../../../hooks/useSearchRpps'
import { defaultPalette } from '../../../theme/idsTheme'
import { formatPhysician } from '../../../utils/requiredExpert'
import styles from '../_styles/searchRppsStyles'
import { SearchRppsProps } from './_props'
import SearchRppsOption from './SearchRppsOption'
import SearchRppsTitle from './SearchRppsTitle'

const useStyles = makeStyles(styles)

const SearchRpps = ({
    onRppsUserChange,
    selectedValue,
    codeprofession = undefined,
    disabled = false,
    required = false,
    error,
    errorMessage = 'Veuillez compléter le champ manquant',
    helperTextPosition,
    onClear,
    onAutocompleteSelected,
    displayTitle = false,
    title,
}: SearchRppsProps) => {
    const classes = useStyles()
    const [searchText, setSearchText] = useState('')

    const defaultSearchText =
        selectedValue && selectedValue?.nomdexercice && selectedValue?.prenomdexercice
            ? `${selectedValue?.nomdexercice} ${selectedValue?.prenomdexercice}`
            : ''

    const getSearchPhrase = () => {
        const searchBase =
            selectedValue && searchText === formatPhysician(selectedValue) ? defaultSearchText : searchText
        const searchPhrase = searchBase.split('(')[0]
        return searchPhrase.trim().toLocaleLowerCase()
    }

    const { rppsUsers, loading, helper } = useSearchRpps({
        searchText: getSearchPhrase(),
        value: selectedValue,
        codeprofession,
    })

    const onAutocompleteChange = (_event: React.SyntheticEvent, value: string) => {
        setSearchText(value)
        if (!value || value.trim() === '') {
            if (onClear) onClear()
        }
    }

    const placeholder = `Nom, prénom ou identifiant RPPS ${required ? '*' : ''}`

    return (
        <Stack flexDirection="column" gap="1.5rem" width="100%">
            <SearchRppsTitle display={displayTitle} title={title} />
            <Autocomplete
                id="rpps-search"
                freeSolo
                inputValue={searchText}
                value={selectedValue}
                onInputChange={onAutocompleteChange}
                onChange={(_e, userRpps) => {
                    onRppsUserChange(userRpps)
                }}
                onSelect={() => {
                    if (onAutocompleteSelected) onAutocompleteSelected()
                }}
                isOptionEqualToValue={(option, newValue) => option.identifiantpp === newValue.identifiantpp}
                getOptionLabel={(option) => {
                    if (typeof option !== 'string') {
                        return formatPhysician(option) || ''
                    }
                    return ''
                }}
                filterOptions={(filterOptions) => filterOptions}
                options={rppsUsers.physicians as UserRpps[]}
                loading={loading || helper}
                loadingText={
                    helper
                        ? 'Commencez par saisir au moins deux caractères pour voir des résultats apparaître'
                        : 'Recherche en cours ...'
                }
                noOptionsText="Aucun résultat"
                ListboxProps={{
                    style: {
                        borderRadius: '0px 0px .5rem .5rem',
                        minHeight: '22rem',
                        overflowX: 'hidden',
                        paddingBottom: '4px',
                    },
                }}
                slotProps={{
                    paper: {
                        elevation: 2,
                        style: {
                            margin: '0 .5rem',
                            borderRight: `1px solid ${defaultPalette.lightGrey.half}`,
                            borderLeft: `1px solid ${defaultPalette.lightGrey.half}`,
                            borderBottom: `1px solid ${defaultPalette.lightGrey.half}`,
                        },
                    },
                }}
                renderOption={(props, option, state, ownerState) => {
                    const { id, ...optionProps } = props
                    return (
                        <SearchRppsOption
                            key={id}
                            nbDisplayedOptions={ownerState.options.length}
                            nbFoundOptions={rppsUsers.count}
                            hasSubheader={state.index === 0}
                            option={option}
                            optionProps={optionProps}
                            inputValue={state.inputValue}
                        />
                    )
                }}
                disabled={disabled}
                fullWidth
                renderInput={(params) => (
                    <TextField
                        variant="outlined"
                        {...params}
                        id="search-rpps"
                        className={classes.gridItemContent}
                        placeholder={placeholder}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                            startAdornment: (
                                <>
                                    <SearchIcon
                                        style={{
                                            fill: defaultPalette.lightGrey.darker,
                                        }}
                                    />
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                            classes: {
                                input: clsx(classes.input),
                            },
                        }}
                        error={error}
                        helperText={error ? errorMessage : null}
                        FormHelperTextProps={{
                            style: {
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                textAlign: helperTextPosition,
                            },
                        }}
                    />
                )}
            />
        </Stack>
    )
}

export default SearchRpps
